import { OrderResponseDto } from '@planning/app/api'
import { IInspectContainerFormData } from '@planning/pages/GateClerk/Components/InspectContainer'
import { action, computed, makeObservable, observable } from 'mobx'
import { ValidateOutboundOrderFunc } from '../gateControl/ValidateOutboundDto'
import { NonNumericOrderWithPickUpAmount } from '../truckAppointment/TruckAppointmentDetailsViewStore'
import { IGateClerkViewStore } from './GateClerkViewStore'
import { GateOperationViewStore } from './GateOperationViewStore'

export class GateInViewStore extends GateOperationViewStore {
  licensePlate = ''
  trailerPlate = ''
  dropOffOrders: IInspectContainerFormData[] = []
  pickUpOrders: IInspectContainerFormData[] = []
  nnrOrders: NonNumericOrderWithPickUpAmount[] = []
  // Clerk's designated-exit-gate choice. Lives here (not in the form) because
  // GateInForm unmounts on every sub-screen push and react-hook-form state dies with it.
  designatedExitGateId: string | null = null

  validateOutboundRequest?: ValidateOutboundOrderFunc

  constructor(public parentStore: IGateClerkViewStore) {
    super()

    makeObservable(this, {
      licensePlate: observable,
      trailerPlate: observable,
      dropOffOrders: observable,
      pickUpOrders: observable,
      nnrOrders: observable,
      designatedExitGateId: observable,
      setLicensePlate: action,
      setTrailerPlate: action,
      setDesignatedExitGateId: action,
      upsertDropOffOrder: action,
      upsertPickUpOrder: action,
      upsertNNROrder: action,
      deleteDropOffOrder: action,
      deletePickUpOrder: action,
      deleteNNROrder: action,
      reset: action,
      generalCargoOrders: computed,
    })
  }

  setLicensePlate = (licensePlate: string) => {
    this.licensePlate = licensePlate
  }

  setTrailerPlate = (trailerPlate: string) => {
    this.trailerPlate = trailerPlate
  }

  setDesignatedExitGateId = (gateId: string | null) => {
    this.designatedExitGateId = gateId
  }

  upsertDropOffOrder = (order: IInspectContainerFormData) => {
    this.dropOffOrders = [...this.dropOffOrders.filter(o => o.id !== order.id), order]
  }

  deleteDropOffOrder = (orderId: number) => {
    this.dropOffOrders = [...this.dropOffOrders.filter(o => o.id !== orderId)]
  }

  upsertPickUpOrder = (order: IInspectContainerFormData) => {
    this.pickUpOrders = [...this.pickUpOrders.filter(o => o.id !== order.id), order]
  }

  deletePickUpOrder = (orderId: number) => {
    this.pickUpOrders = [...this.pickUpOrders.filter(o => o.id !== orderId)]
  }

  upsertNNROrderByOrder = (order: OrderResponseDto) => {
    let nnrOrder = this.nnrOrders.find(o => o.id === order.nonNumericOrderId)

    if (nnrOrder) {
      nnrOrder.pickUpAmount = nnrOrder.pickUpAmount + 1
    } else {
      nnrOrder = {
        id: order.nonNumericOrderId,
        referenceNumber: order.referenceNumber,
        pickUpAmount: 1,
        suitableContainersCount: order.nonNumericSuitableContainersCount,
      } as NonNumericOrderWithPickUpAmount
    }

    this.upsertNNROrder(nnrOrder)
  }

  upsertNNROrder = (order: NonNumericOrderWithPickUpAmount) => {
    this.nnrOrders = [...this.nnrOrders.filter(o => o.id !== order.id), order]
  }

  deleteNNROrder = (orderId: number) => {
    this.nnrOrders = [...this.nnrOrders.filter(o => o.id !== orderId)]
  }

  reset = () => {
    this.resetDialogs()
    this.licensePlate = ''
    this.dropOffOrders = []
    this.pickUpOrders = []
    this.nnrOrders = []
    this.designatedExitGateId = null
  }

  get generalCargoOrders() {
    return [
      ...this.dropOffOrders.filter(o => o.commodityId),
      ...this.pickUpOrders.filter(o => o.commodityId),
    ]
  }
}
